/**
 * @name Backup
 * @description Componente para agregar, editar y eliminar backup en configuración de perfil
 * @author Gerardo de León
 */

import React, { Component } from 'react';
import i18n from 'i18next';
import styled from 'styled-components';
import moment from 'moment';
import { trackPromise } from 'react-promise-tracker';

import { Environment } from '@tycommon/config';
import { DataGrid, FiltersExpansion, LoadingWrapper } from '@tycommon/components';
import { BackupService, EmployeeService, NotificationsService, ResponseHandlerService } from '@tycommon/services';
import { MUI, Flex, Icons } from '@tycommon/styles';

// Components
import BackupDialog from './components/backup.dialog';
import CancelDialog from './components/cancel.dialog';
import BackupLogDialog from './components/backup-log.dialog';

class Backup extends Component {
  constructor(props) {
    super(props);
    this.state = {
      employeesList: [],
      responsabilitiesList: [],
      selectedBackup: [],
      gridConfig: {
        rows: [],
        columns: [
          { id: 'Id', label: i18n.t('Id'), visible: false, filter: null },
          { id: 'IdEmpleadoBackup', label: i18n.t('Id'), visible: false, filter: null },
          { type: 'complex', id: ['FotoBackup', 'NombreBackup', 'PuestoBackup'], label: i18n.t('Backup'), visible: true, filter: null, sort: false },
          { id: 'Responsabilidades', label: i18n.t('GenericMessages.responsibilities'), visible: true, filter: null, sort: false },
          { id: 'FechaInicio', label: i18n.t('GenericMessages.startDate'), visible: true, filter: 'ShortDateFilter', sort: false },
          { id: 'FechaFin', label: i18n.t('GenericMessages.endDate'), visible: true, filter: 'ShortDateFilter', sort: false },
          { id: 'Estatus', label: i18n.t('GenericMessages.status'), visible: true, filter: null, sort: true },
          {
            type: 'render', id: 'Comentarios', label: i18n.t('BackupSettings.backupLog'), visible: true, filter: null, sort: false,
            render: (value, row) => <MUI.ButtonLink onClick={() => this.showBackupLogDialog(value, row)}>{i18n.t('GenericMessages.seeDetails')}</MUI.ButtonLink>
          }
        ],
        canAdd: true,
        canEdit: false,
        canDelete: false,
        canSelect: true,
        canCancel: true
      },
      showBackupDialog: {
        data: {},
        open: false
      },
      showCancelDialog: {
        data: {},
        open: false
      },
      showBackupLogDialog: {
        data: {},
        open: false
      },
      backupNamesList: [],
      filters: {
        backup: null,
        responsability: null,
        startDate: moment().month(0).date(1),
        endDate: moment().month(11).date(31),
        status: 0
      },
      comments: null,
    };
  }

  componentDidMount() {
    this.updateFilters(this.state.filters);
    this.getResponsabilities();
  }

  // Consulta los backup del usuario
  getBackup = (filters) => {
    const { gridConfig } = this.state;
    trackPromise(
      BackupService.get(filters)
        .then((response) => {
          // Obtiene las listas para los filtros
          let listNamesBackup = Array.from(new Set(response.map((request) => request.NombreBackup))).map((name) => response.find((a) => a.NombreBackup === name));
          // NOTA: Se resta un día a la Fecha Fin para mostrarla de forma correcta y se ordenan los comentarios por fecha.
          //response.map((item) => {
            //item.FechaFin = moment(item.FechaFin).subtract(1, 'days');
            //item.Comentarios = item.Comentarios.sort((a, b) => new Date(a.Fecha) - new Date(b.Fecha));
          //});

          gridConfig.rows = response;
          this.setState((prevState) => ({ gridConfig: { ...prevState.gridConfig, gridConfig }, backupNamesList: listNamesBackup }));
        })
        .catch((error) => {
          ResponseHandlerService.error(error);
          gridConfig.rows = [];
          this.setState((prevState) => ({ gridConfig: { ...prevState.gridConfig, gridConfig } }));
        }),
      'loading-report'
    );
  }

  /* 
    Estatus 1 - Confirmado
    Estatus 2 - Rechazado
    Estatus 3 - Cancelado
    Estatus 4 - Finalizado

    [CANCELAR] No se puede cancelar si esta corriendo el periodio de backup o ya termino.
    [RECHAZAR] No se puede rechazar si el periodo de backup empieza mañana o ya termino.
  */

  getBackupFiltersBackup = () => {
    this.updateFilters({
      employee: null,
      backup: null,
      responsability: null,
      startDate: moment().month(0).date(1),
      endDate: moment().month(11).date(31),
      status: 0
    });
  }

  // Cancela la solicitud de backup
  cancelBackup = (values, backup, setSubmitting) => {
    let request = {
      IdBackup: backup.IdBackup,
      Comentario: values.comment,
      Estatus: 3
    };

    // Notificación - <colaborador> cancelo la solicitud de backup
    let notificationToSend = {
      IdAmbiente: Environment.getCurrent().environmentId,
      URL: '#/configuraperfil',
      IdTipoNotificacion: 135,
      IdEmpleado: backup.IdEmpleadoBackup,
      Parametros: JSON.stringify({ Tab: 6 }),
      Propiedades: JSON.stringify({ colaborador: backup.NombreBackup })
    };

    BackupService.put(request)
      .then((response) => {
        setSubmitting(false);
        this.closeDialog('cancel');
        this.updateFilters({
          employee: null,
          backup: null,
          responsability: null,
          startDate: moment().month(0).date(1),
          endDate: moment().month(11).date(31),
        });
        NotificationsService.post(notificationToSend);
        ResponseHandlerService.success(response);
      })
      .catch((error) => {
        setSubmitting(false);
        ResponseHandlerService.error(error);
      });
  }

  // Consulta todos los empleados en estatus 'Alta'
  getEmployees = () => {
    EmployeeService.get({ page: 0, size: 0, filter: { estatus: true } })
      .then((response) => {
        let employeesList = response.filter((employee) => employee.IdEmpleado !== this.userInfo.IdEmpleado);
        this.setState({ employeesList });
      })
      .catch(() => { });
  }

  // Consulta todas las responsabilidades
  getResponsabilities = () => {
    BackupService.getResponsabilieties()
      .then((response) => {
        this.setState({ responsabilitiesList: response });
      })
      .catch(() => { });
  }

  selectBackup = (backup) => {
    if (backup !== undefined) {
      let selectedBackup = backup[0];
      let canCancelFlag = selectedBackup && selectedBackup.IdEstatus !== 1 ? false : true;
      this.setState({
        selectedBackup, gridConfig: {
          rows: this.state.gridConfig.rows,
          columns: this.state.gridConfig.columns,
          canAdd: this.state.gridConfig.canAdd,
          canEdit: this.state.gridConfig.canEdit,
          canDelete: this.state.gridConfig.canEdit,
          canSelect: this.state.gridConfig.canSelect,
          canCancel: canCancelFlag
        }
      });
    }
  }

  showBackupDialog = () => {
    let data = {};
    this.setState({ showBackupDialog: { data, open: true } });
  };

  showCancelDialog = (item) => {
    let data = item ? { ...item[0] } : {};
    this.setState({ showCancelDialog: { data, open: true } });
  }

  showBackupLogDialog = (value, row) => {
    let data = {};
    let comments = row;
    this.setState({ showBackupLogDialog: { data, open: true }, comments });
  };

  closeDialog = (dialog) => {
    if (dialog === 'backup') {
      this.setState({ showBackupDialog: { data: {}, open: false } });
    }
    else if (dialog === 'backupLog') {
      this.setState({ showBackupLogDialog: { data: {}, open: false } });
    }
    else if (dialog === 'cancel') {
      this.setState({ showCancelDialog: { data: {}, open: false } });
    }
  }

  /** 
 * Actualiza los filtros del reporte
 * @param filters (employee, status)
 */
  updateFilters = ({ backup, responsability, startDate, endDate, status }) => {
    const filters = {
      backup: backup && backup !== null ? backup.IdEmpleadoBackup : null,
      responsability: responsability && responsability !== null ? responsability.IdResponsabilidad : null,
      startDate: startDate ? moment(startDate).format('YYYY/MM/DD') : null,
      endDate: endDate ? moment(endDate).format('YYYY/MM/DD') : null,
      status: status !== 0 ? status : null
    };
    this.getBackup(filters);
    this.setState({ filters: { backup, responsability, startDate, endDate, status } });
  }

  render() {
    const { employeesList, gridConfig, selectedBackup, backupNamesList, startDate, endDate, responsabilitiesList, filters, comments } = this.state;

    return (
      <>
        <LoadingWrapper area="loading-tab-backup">

          {/* Filtros */}
          <Flex.Row flex="1">
            <Styled.BoxFilters>
              <FiltersExpansion>
                <MUI.Box padding="8px" width="100%">
                  <MUI.Grid container alignItems="center" spacing={4}>
                    {/* Empleado Backup */}
                    <MUI.Grid item md={2} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.Autocomplete
                          label={i18n.t('Backup')}
                          searchList={backupNamesList}
                          searchBy="NombreBackup"
                          value={filters.backup}
                          onChange={(newBackup) => this.updateFilters({
                            backup: newBackup,
                            responsability: filters.responsability,
                            startDate: filters.startDate,
                            endDate: filters.endDate,
                            status: filters.status
                          })}
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    {/* Responsabilidad */}
                    <MUI.Grid item md={4} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.Autocomplete
                          label={i18n.t('GenericMessages.responsibilities')}
                          searchList={responsabilitiesList}
                          searchBy="Responsabilidad"
                          value={filters.responsability}
                          onChange={(newResponsability) => this.updateFilters({
                            backup: filters.backup,
                            responsability: newResponsability,
                            startDate: filters.startDate,
                            endDate: filters.endDate,
                            status: filters.status
                          })}
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    <MUI.Grid item md={2} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.DatePicker
                          allowKeyboard={false}
                          label={i18n.t('GenericMessages.startDate')}
                          format={i18n.t('DD/MMMM/YYYY')}
                          invalidDateMessage={i18n.t('FormatDates.invalidDate')}
                          value={filters.startDate}
                          onChange={(newDate) => this.updateFilters({
                            backup: filters.backup,
                            responsability: filters.responsability,
                            startDate: newDate,
                            endDate: filters.endDate,
                            status: filters.status
                          })}
                          openTo="date"
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    <MUI.Grid item md={2} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.DatePicker
                          allowKeyboard={false}
                          label={i18n.t('GenericMessages.endDate')}
                          format={i18n.t('DD/MMMM/YYYY')}
                          invalidDateMessage={i18n.t('FormatDates.invalidDate')}
                          value={filters.endDate}
                          onChange={(newDate) => this.updateFilters({
                            backup: filters.backup,
                            responsability: filters.responsability,
                            startDate: filters.startDate,
                            endDate: newDate,
                            status: filters.status
                          })}
                          openTo="date"
                        />
                      </Styled.BoxFilter>
                    </MUI.Grid>
                    {/* Estatus */}
                    <MUI.Grid item md={2} sm={2} xs={12}>
                      <Styled.BoxFilter>
                        <MUI.TextFieldIcon
                          select
                          label={i18n.t('GenericMessages.status')}
                          type="text"
                          name="status"
                          value={filters.status}
                          InputProps={{ startAdornment: (<Icons.FilterList />) }}
                          onChange={(event) => this.updateFilters({
                            backup: filters.backup,
                            responsability: filters.responsability,
                            startDate: filters.startDate,
                            endDate: filters.endDate,                            
                            status: event.target.value
                          })}
                        >
                          <MUI.SelectOption value={0}>
                            {i18n.t('GenericMessages.all')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={1}>
                            {i18n.t('Confirmado')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={2}>
                            {i18n.t('StatusText.rejected')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={3}>
                            {i18n.t('Cancelado')}
                          </MUI.SelectOption>
                          <MUI.SelectOption value={4}>
                            {i18n.t('Finalizado')}
                          </MUI.SelectOption>
                        </MUI.TextFieldIcon>
                      </Styled.BoxFilter>
                    </MUI.Grid>
                  </MUI.Grid>
                </MUI.Box>
              </FiltersExpansion>
            </Styled.BoxFilters>
          </Flex.Row>

          <ContentWrapper>
            <MUI.Grid container>
              {/* Backup */}
              <MUI.Grid item sm={12} xs={12}>
                <BoxContainer>
                  <DataGrid
                    columns={gridConfig.columns}
                    rows={gridConfig.rows}
                    canAdd={gridConfig.canAdd}
                    canEdit={gridConfig.canEdit}
                    canDelete={gridConfig.canDelete}
                    canSelect={gridConfig.canSelect}
                    canCancel={gridConfig.canCancel}
                    canSort={true}
                    isPaginated={true}
                    onAdd={() => this.showBackupDialog()}
                    onCancel={(item) => this.showCancelDialog(item)}
                    onClickRow={(item) => this.selectBackup(item)}
                  />
                </BoxContainer>
              </MUI.Grid>

            </MUI.Grid>
          </ContentWrapper>
        </LoadingWrapper>

        {/* Modal para añadir un backup */}
        {this.state.showBackupDialog.open ? (
          <BackupDialog
            open={this.state.showBackupDialog.open}
            onClose={() => this.closeDialog('backup')}
            getBackup={() => this.getBackupFiltersBackup()}
          />
        ) : null}

        {/* Modal para cancelar un backup */}
        {this.state.showCancelDialog.open ? (
          <CancelDialog
            data={this.state.showCancelDialog.data}
            open={this.state.showCancelDialog.open}
            onClose={() => this.closeDialog('cancel')}
            onCancelBackup={this.cancelBackup}
          />
        ) : null}

        {/* Modal para mostrar la bitácora del backup */}
        {this.state.showBackupLogDialog.open ? (
          <BackupLogDialog
            open={this.state.showBackupLogDialog.open}
            onClose={() => this.closeDialog('backupLog')}
            getBackup={() => this.getBackup()}
            comments={comments}
          />
        ) : null}
      </>
    );
  }
}

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxFilters = styled.div`
  width: 100%;
  margin-bottom: 8px;
`;

Styled.BoxFilter = styled.div`
  .MuiFormControl-root {
    min-height: 46px;
  }
`;

const ContentWrapper = styled.section`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  padding: 8px;
  //border: 1px solid ${(props) => props.theme.colors.greyLight};
  //border-radius: 6px;
  //box-shadow: ${(props) => props.theme.shadows.depth1};
`;

const BoxContainer = styled.div`
  padding: 16px;
  margin: 8px;
  border: 1px solid ${(props) => props.theme.colors.greyLight};
  border-radius: 6px;
  box-shadow: ${(props) => props.theme.shadows.depth1};
`;

export default Backup;